import dayjs, { type ManipulateType } from 'dayjs'

export type DueDatePreset = {
  label: string
  amount: number
  unit: ManipulateType
}

// Quick due-date options shown as a segmented control in the Task Modal.
export const DUE_DATE_PRESETS: DueDatePreset[] = [
  { label: '1h', amount: 1, unit: 'hour' },
  { label: '1d', amount: 1, unit: 'day' },
  { label: '1w', amount: 1, unit: 'week' },
  { label: '2w', amount: 2, unit: 'week' },
  { label: '1mo', amount: 1, unit: 'month' },
  { label: '2mo', amount: 2, unit: 'month' },
  { label: '3mo', amount: 3, unit: 'month' },
]

// When a date is picked from the calendar without a time (midnight), default
// it to the given hour (9 AM) so picks land at a sensible time of day.
export const withDefaultTime = (
  date: Date | string | null,
  hour = 9
): Date | null => {
  if (!date) return null
  const d = new Date(date)
  if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) {
    d.setHours(hour, 0, 0, 0)
  }
  return d
}

// A preset due date lands `amount/unit` from now. Day-or-larger spans snap to
// 5:00 PM; sub-day spans (hours/minutes) keep their computed time so "1h" means
// an hour from now rather than 5:00 PM today.
export const dueDateFromPreset = (preset: DueDatePreset): Date => {
  const base = dayjs().add(preset.amount, preset.unit)
  const snapsToEvening = preset.unit !== 'hour' && preset.unit !== 'minute'
  return (snapsToEvening ? base.hour(17).minute(0) : base)
    .second(0)
    .millisecond(0)
    .toDate()
}

// Default reminder when a due date is chosen: 9:00 AM on the due date — but
// only when that's before the due time and at least an hour out. Otherwise
// (due is soon, or earlier in the day than 9 AM) fall back to one hour before.
export const computeDefaultReminder = (
  dueDate: Date | string | null
): Date | null => {
  if (!dueDate) return null
  const due = dayjs(dueDate)
  const morningOf = due.hour(9).minute(0).second(0).millisecond(0)
  if (morningOf.isBefore(due) && morningOf.isAfter(dayjs().add(1, 'hour'))) {
    return morningOf.toDate()
  }
  return due.subtract(1, 'hour').toDate()
}
